import { useEffect } from "react";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { useDialog } from "app/hook";
import { RootState, AppDispatch } from "app/store";
import { AppPlatform, DialogType } from "app/types";
import { sleep } from "common/utils";
import i18n from "features/shared/i18n";
import { PlanCategory, BillingPeriod } from "features/subscription/useNativePackages";
import {
  useNativePackages,
  fetchAccountPlan,
  createHostedCheckout,
  useSubscriptionPlans,
  isSubscriptionSupported,
} from "features/subscription";

const showToast = (message: string) => {
  toast(
    <div>
      <p className="text text-textcolor-black">{message}</p>
    </div>,
  );
};

// チャットの購入 CTA から呼ぶ課金フロー。SubscriptionPage の購入ロジックを CTA 用に抽出した hook。
// Web は Stripe Checkout、iOS/Android アプリは RevenueCat（App Store/Play 規約遵守）。
export const usePurchaseFlow = () => {
  const dispatch = useDispatch<AppDispatch>();
  const handleDialog = useDialog();
  const user = useSelector((state: RootState) => state.user.parent);
  const platform = window.parentsAppPlatform || AppPlatform.Web;
  const isNativeApp = platform === AppPlatform.iOS || platform === AppPlatform.Android;
  const { getIdentifier, requestPackages } = useNativePackages();
  const { data: webPlans = [] } = useSubscriptionPlans("web", !isNativeApp);

  useEffect(() => {
    if (isNativeApp) {
      requestPackages();
    }
  }, [isNativeApp, requestPackages]);

  const getProductId = (
    planCategory: PlanCategory,
    period: BillingPeriod,
  ): string | undefined => {
    if (isNativeApp) {
      return getIdentifier(planCategory, period);
    }
    const targetName = planCategory === "standard" ? "基本プラン" : "特別プラン";
    const foundPlan = webPlans.find(
      (p) => p.name.includes(targetName) && p.billing_period === period,
    );
    return foundPlan?.product_id;
  };

  const purchaseRevenueCat = (productId: string) => {
    handleDialog({ type: DialogType.LOADING, value: true });
    try {
      if (isSubscriptionSupported()) {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ command: "subscription", data: { uid: user.uid, sku: productId } }),
        );
      } else {
        handleDialog({ type: DialogType.LOADING, value: false });
        showToast(i18n.t("MSG_SUBSCRIPTION_PURCHASE_ERROR"));
      }
    } catch (err) {
      console.error("Failed to create subscription intent:", err);
      handleDialog({ type: DialogType.LOADING, value: false });
      showToast(i18n.t("MSG_SUBSCRIPTION_PURCHASE_ERROR"));
    }
  };

  const purchaseHostedCheckout = async (productId: string) => {
    handleDialog({ type: DialogType.LOADING, value: true });
    try {
      const result = await dispatch(
        createHostedCheckout({
          uid: user.uid,
          product_id: productId,
          success_url: `${window.location.origin}/subscription`,
          cancel_url: `${window.location.origin}/subscription`,
        }),
      ).unwrap();
      window.location.href = result.checkout_url;
    } catch (err) {
      console.error("Failed to create hosted checkout:", err);
      handleDialog({ type: DialogType.LOADING, value: false });
      showToast(i18n.t("MSG_SUBSCRIPTION_PURCHASE_ERROR"));
    }
  };

  const purchase = (planCategory: PlanCategory, billingPeriod: BillingPeriod) => {
    const productId = getProductId(planCategory, billingPeriod);
    if (!productId) {
      showToast(i18n.t("MSG_SUBSCRIPTION_PURCHASE_ERROR"));
      return;
    }
    if (isNativeApp) {
      purchaseRevenueCat(productId);
    } else {
      purchaseHostedCheckout(productId);
    }
  };

  // ネイティブ購入結果リスナー
  useEffect(() => {
    const handleMessage = async (message: MessageEvent) => {
      try {
        const { command, data } = JSON.parse(message.data);
        if (command !== "subscription") return;
        if (data?.result === "success") {
          await sleep(5000);
          await dispatch(fetchAccountPlan({ uid: user.uid, forceSync: true })).unwrap();
          handleDialog({ type: DialogType.LOADING, value: false });
          showToast(i18n.t("MSG_SUBSCRIPTION_PURCHASE_SUCCESS"));
        } else {
          handleDialog({ type: DialogType.LOADING, value: false });
          showToast(i18n.t("MSG_SUBSCRIPTION_PURCHASE_ERROR"));
        }
      } catch {
        // Non-JSON messages are ignored
      }
    };
    if (window.ReactNativeWebView) {
      if (window.parentsAppPlatform === "ios") {
        window.addEventListener("message", handleMessage);
      } else {
        document.addEventListener("message", handleMessage as EventListener);
      }
    }
    return () => {
      if (window.ReactNativeWebView) {
        if (window.parentsAppPlatform === "ios") {
          window.removeEventListener("message", handleMessage);
        } else {
          document.removeEventListener("message", handleMessage as EventListener);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { purchase };
};
